import React, { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import { Link } from "react-router-dom";
import api from "@/api/client";
import {
  TrendingUp, Link2, MousePointerClick, Wallet, Percent, FileText, Building2, ArrowUpRight,
} from "lucide-react";
import WeekendRecap from "@/components/WeekendRecap";
import QuickCompare from "@/components/QuickCompare";

const revenueSeries = [
  { m: "Jan", rev: 320, aff: 180 },
  { m: "Feb", rev: 480, aff: 260 },
  { m: "Mar", rev: 720, aff: 410 },
  { m: "Apr", rev: 1120, aff: 690 },
  { m: "May", rev: 1580, aff: 940 },
  { m: "Jun", rev: 2380, aff: 1520 },
];

function Metric({ label, value, delta, hint, icon: Icon, accent = "text-sky-400" }) {
  return (
    <div className="border border-slate-800 rounded-lg p-5 bg-slate-900/40 hover:border-sky-400/40 transition group">
      <div className="flex items-center justify-between mb-4">
        <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{label}</span>
        <Icon className={`w-4 h-4 ${accent} opacity-70 group-hover:opacity-100 transition`} strokeWidth={1.5} />
      </div>
      <div className="font-serif text-3xl font-medium text-sky-50 font-numeric">{value}</div>
      {delta && (
        <div className="flex items-center gap-1 mt-2 text-[11px] font-mono text-emerald-400">
          <ArrowUpRight className="w-3 h-3" /> {delta}
        </div>
      )}
      {hint && <p className="text-xs text-neutral-500 mt-1.5">{hint}</p>}
    </div>
  );
}

export default function DashboardHome() {
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, a] = await Promise.all([
          api.get("/dashboard/stats"),
          api.get("/affiliate/analytics/summary"),
        ]);
        setStats(s.data);
        setAnalytics(a.data);
      } catch {}
    })();
  }, []);

  return (
    <div className="space-y-10 fade-up">
      {/* Editorial header */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 border-b border-slate-800 pb-8">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-sky-400 mb-3">
            — The Terminal
          </p>
          <h1 className="font-serif text-5xl lg:text-6xl leading-none text-sky-100 tracking-tight">
            Overview
          </h1>
          <p className="mt-3 text-neutral-400 max-w-2xl">
            Live telemetry across every autonomous affiliate site you've launched. Data refreshed on demand.
          </p>
        </div>
        <Link
          to="/app/affiliate"
          data-testid="cta-open-affiliate"
          className="inline-flex items-center gap-2 border border-sky-400 text-sky-100 hover:bg-sky-400 hover:text-slate-950 px-5 py-2.5 rounded-md transition"
        >
          Open Affiliate Studio <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Weekend recap */}
      <WeekendRecap />

      {/* Unique feature: quick product comparison */}
      <QuickCompare />

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Metric
          label="Total Commission"
          value={`$${(stats?.affiliate_earnings ?? 0).toLocaleString()}`}
          delta="+34.2% vs prior 30d"
          hint="Across all networks"
          icon={Wallet}
        />
        <Metric
          label="Clicks"
          value={(stats?.total_clicks ?? 0).toLocaleString()}
          delta="+18.6%"
          hint="Real-time tracked"
          icon={MousePointerClick}
          accent="text-teal-400"
        />
        <Metric
          label="Conversions"
          value={(stats?.total_conversions ?? 0).toLocaleString()}
          delta="+12.9%"
          hint={`Rate ${analytics?.conversion_rate ?? 0}%`}
          icon={Percent}
          accent="text-emerald-400"
        />
        <Metric
          label="Content Pieces"
          value={stats?.affiliate_content ?? 0}
          hint="Published by AI"
          icon={FileText}
          accent="text-sky-300"
        />
      </div>

      {/* Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-slate-800 rounded-xl bg-slate-900/30 p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Fig. 01</p>
              <h3 className="font-serif text-xl text-sky-100 mt-1">Revenue trajectory</h3>
            </div>
            <span className="font-mono text-[11px] text-neutral-500">USD · 6m</span>
          </div>
          <div className="h-72">
            <ResponsiveContainer>
              <LineChart data={revenueSeries}>
                <CartesianGrid strokeDasharray="2 4" stroke="#333" />
                <XAxis dataKey="m" stroke="#666" fontSize={11} />
                <YAxis stroke="#666" fontSize={11} />
                <Tooltip
                  contentStyle={{ backgroundColor: "#1a1a1a", borderColor: "#333", fontSize: 12, fontFamily: "JetBrains Mono" }}
                  labelStyle={{ color: "#e6b800" }}
                />
                <Line dataKey="rev" stroke="#e6b800" strokeWidth={2.5} dot={{ r: 3, fill: "#e6b800" }} name="Total Revenue" />
                <Line dataKey="aff" stroke="#45a8a8" strokeWidth={2.5} strokeDasharray="4 4" dot={{ r: 3, fill: "#45a8a8" }} name="Affiliate Only" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border border-slate-800 rounded-xl p-6">
          <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Fig. 02</p>
          <h3 className="font-serif text-xl text-sky-100 mt-1 mb-5">By network</h3>
          {analytics?.network_breakdown?.length > 0 ? (
            <div className="space-y-4">
              {analytics.network_breakdown.map((n) => (
                <div key={n.network}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-sky-100">{n.network}</span>
                    <span className="font-mono text-sky-300">${n.commission.toFixed(2)}</span>
                  </div>
                  <div className="h-1.5 bg-slate-800 rounded overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-sky-500 to-sky-300"
                      style={{ width: `${Math.min(100, (n.commission / Math.max(1, analytics.total_commission)) * 100)}%` }}
                    />
                  </div>
                  <p className="text-[11px] text-neutral-500 mt-1 font-mono">
                    {n.clicks} clicks · {n.conversions} conv
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-neutral-500 leading-relaxed">
              No affiliate accounts yet. <Link to="/app/affiliate" className="text-sky-300 border-b border-sky-400/40">Connect one</Link> to see live network performance.
            </p>
          )}
        </div>
      </div>

      {/* Businesses summary */}
      <div className="border border-slate-800 rounded-xl p-6">
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">Fig. 03</p>
            <h3 className="font-serif text-xl text-sky-100 mt-1">Global footprint</h3>
          </div>
          <div className="flex gap-6 font-mono text-[11px] uppercase text-neutral-500">
            <span>{stats?.total_businesses ?? 0} businesses</span>
            <span>{stats?.total_posts ?? 0} posts</span>
            <span>{stats?.seo_average_score ?? 0}% avg SEO</span>
          </div>
        </div>
        <div className="h-40">
          <ResponsiveContainer>
            <BarChart data={revenueSeries}>
              <XAxis dataKey="m" stroke="#666" fontSize={10} />
              <Tooltip
                contentStyle={{ backgroundColor: "#1a1a1a", borderColor: "#333", fontSize: 12 }}
                cursor={{ fill: "rgba(230,184,0,0.08)" }}
              />
              <Bar dataKey="rev" fill="#e6b800" opacity={0.85} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
